/**
 * Font Metric Calibration
 * 
 * Since font metrics aren't always directly available, we calibrate by
 * rendering test glyphs offscreen and measuring pixel bounds.
 * 
 * This ensures:
 * - Baseline aligns to bottom line
 * - X-height aligns to midline  
 * - Cap height aligns to top line
 */

// Cache for calibration results
const calibrationCache = new Map();

/**
 * Create offscreen canvas for measurement
 */
const createMeasurementCanvas = () => {
  const canvas = document.createElement('canvas');
  canvas.width = 500;
  canvas.height = 500;
  return canvas;
};

/**
 * Measure glyph bounds by analyzing pixel data
 */
const measureGlyphBounds = (ctx, text, fontSize, fontFamily) => {
  const canvas = ctx.canvas;
  
  // Clear canvas
  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  
  // Draw text
  ctx.font = `${fontSize}px "${fontFamily}"`;
  ctx.fillStyle = 'black';
  ctx.textBaseline = 'alphabetic';
  
  // Position at center with baseline at y=300
  const baselineY = 300;
  ctx.fillText(text, 50, baselineY);
  
  // Get pixel data
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const pixels = imageData.data;
  
  let minY = canvas.height;
  let maxY = 0;
  let minX = canvas.width;
  let maxX = 0;
  
  // Find bounds of black pixels
  for (let y = 0; y < canvas.height; y++) {
    for (let x = 0; x < canvas.width; x++) {
      const idx = (y * canvas.width + x) * 4;
      // Check if pixel is dark (part of text)
      if (pixels[idx] < 128 || pixels[idx + 1] < 128 || pixels[idx + 2] < 128) {
        minY = Math.min(minY, y);
        maxY = Math.max(maxY, y);
        minX = Math.min(minX, x);
        maxX = Math.max(maxX, x);
      }
    }
  }
  
  return {
    top: minY,
    bottom: maxY,
    left: minX,
    right: maxX,
    width: maxX - minX,
    height: maxY - minY,
    baselineY: baselineY
  };
};

/**
 * Calibrate font metrics for accurate alignment
 * 
 * @param {string} fontFamily - Font family name
 * @param {number} fontSize - Font size in pixels
 * @returns {object} - Calibration data with offsets
 */
export const calibrateFont = (fontFamily, fontSize) => {
  const cacheKey = `${fontFamily}-${fontSize}`;
  
  if (calibrationCache.has(cacheKey)) {
    return calibrationCache.get(cacheKey);
  }
  
  const canvas = createMeasurementCanvas();
  const ctx = canvas.getContext('2d');
  
  // Measure 'x' for x-height
  const xBounds = measureGlyphBounds(ctx, 'x', fontSize, fontFamily);
  
  // Measure 'H' for cap height
  const hBounds = measureGlyphBounds(ctx, 'H', fontSize, fontFamily);
  
  // Calculate metrics relative to baseline
  const baseline = xBounds.baselineY;
  
  // Ascent = distance from baseline to top of 'H' (cap height)
  const capHeight = baseline - hBounds.top;
  
  // X-height = distance from baseline to top of 'x'
  const xHeight = baseline - xBounds.top;
  
  // Descent = distance from baseline to bottom of glyphs
  const descent = Math.max(xBounds.bottom - baseline, 0);
  
  const calibration = {
    fontFamily,
    fontSize,
    capHeight,
    xHeight,
    descent,
    baseline,
    // Ratios for scaling
    capHeightRatio: capHeight / fontSize,
    xHeightRatio: xHeight / fontSize,
    descentRatio: descent / fontSize
  };
  
  calibrationCache.set(cacheKey, calibration);
  return calibration;
};

/**
 * Calculate the Y offset to align text baseline to a target Y position
 * 
 * @param {number} targetBaselineY - Where baseline should be (in pixels)
 * @param {object} calibration - Calibration data from calibrateFont
 * @returns {number} - Y position to use for drawing text
 */
export const getBaselineOffset = (targetBaselineY, calibration) => {
  // When drawing with textBaseline='alphabetic', the y coordinate IS the baseline
  return targetBaselineY;
};

/**
 * Calculate font size to match a specific cap height
 * 
 * @param {number} targetCapHeight - Desired cap height in pixels
 * @param {string} fontFamily - Font family name
 * @returns {number} - Font size in pixels
 */
export const fontSizeForCapHeight = (targetCapHeight, fontFamily) => {
  // First, calibrate at a reference size
  const refSize = 100;
  const refCalibration = calibrateFont(fontFamily, refSize);
  
  // Scale to get desired cap height
  const fontSize = (targetCapHeight / refCalibration.capHeightRatio);
  return fontSize;
};

/**
 * Measure text width using canvas
 * 
 * @param {string} text - Text to measure
 * @param {number} fontSize - Font size in pixels
 * @param {string} fontFamily - Font family name
 * @returns {number} - Width in pixels
 */
export const measureTextWidth = (text, fontSize, fontFamily) => {
  const canvas = createMeasurementCanvas();
  const ctx = canvas.getContext('2d');
  ctx.font = `${fontSize}px "${fontFamily}"`;
  return ctx.measureText(text).width;
};

/**
 * Clear calibration cache (call when font changes)
 */
export const clearCalibrationCache = () => {
  calibrationCache.clear();
};
